<script setup>
import { ref, reactive, computed, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ClosureAPI from 'dashboard/api/closure';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'next/textarea/TextArea.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import {
  parseAmount,
  isAmountInvalid,
  formatAmountPreview,
} from 'dashboard/helper/amountParser';

// SICARG MOD 3 v8 — Modal de cierre con árbol de tipificaciones Sancor de 3 niveles
// y formulario dinámico de acuerdo de pago. Timezone: America/Argentina/Buenos_Aires.
const emit = defineEmits(['saved']);
const { t } = useI18n();

const dialogRef = ref(null);
const isSaving = ref(false);
const settings = ref({ require_on_resolve: false, show_managed_amount: true, show_notes: true, reasons: [] });
const conversationId = ref(null);
const contactId = ref(null);

// ─── Control de doble carga (pago realizado / compromiso de pago) ────
// Registros del mes vigente del cliente; si hay uno del mismo tipo, el asesor elige
// conservar (se registran los dos) o sobrescribir (se elimina el anterior).
const monthPayments = ref([]);
const duplicateAction = ref('');

// ─── Nivel 1: Categoría ──────────────────────────────────────────────
const selectedReason = ref('');
// ─── Nivel 2: Subtipo ────────────────────────────────────────────────
const selectedSubType = ref('');
// SICARG — evita que el watch de selectedReason limpie selectedSubType
// mientras precargamos ambos desde la última clasificación del caso.
const isPresettingClassification = ref(false);
// ─── Campos generales del cierre ─────────────────────────────────────
const managedAmount = ref('');
const notes = ref('');

// ─── Acuerdo activo existente (para aviso de reemplazo) ──────────────
const currentAgreement = ref(null);

const TIPO_LABELS = {
  pago_total:  'Pago total',
  pago_parcial: 'Pago parcial',
  plan_pagos:  'Plan de pagos',
  descuento:   'Descuento',
};

// ─── Formulario de acuerdo de pago (Nivel 3) ─────────────────────────
const agreementForm = reactive({
  tipo_acuerdo: '',
  monto_total: '',
  cantidad_cuotas: '',
  valor_cuota_sugerido: '',
  fecha: '',
  tipo_descuento: '',
  valor_descuento: '',
  monto_acordado_final: '',
});
// Cuotas editables individualmente para plan de pagos
const installments = ref([]);

// ─── Timezone Buenos Aires ────────────────────────────────────────────
// Argentina es UTC-3, sin horario de verano.
const todayBuenosAires = () =>
  new Date().toLocaleDateString('en-CA', { timeZone: 'America/Argentina/Buenos_Aires' });

const addMonthsBA = (dateStr, months) => {
  // Usamos T12:00:00 para evitar problemas de medianoche con cambio de día
  const d = new Date(`${dateStr}T12:00:00`);
  d.setMonth(d.getMonth() + months);
  return d.toLocaleDateString('en-CA', { timeZone: 'America/Argentina/Buenos_Aires' });
};

// ─── Opciones del árbol ───────────────────────────────────────────────
const reasonOptions = computed(() =>
  (settings.value.reasons || [])
    .filter(r => r.active !== false && r.visible_for_agents !== false)
    .map(r => ({ value: r.value, label: r.label }))
);

const selectedReasonObj = computed(() =>
  (settings.value.reasons || []).find(r => r.value === selectedReason.value)
);

const subTypeOptions = computed(() => {
  const r = selectedReasonObj.value;
  return (r?.sub_types || []).map(st => ({ value: st.value, label: st.label }));
});

const duplicateRecords = computed(() =>
  monthPayments.value.filter(r => r.closure_reason === selectedReason.value)
);

const formatDate = value =>
  value ? value.slice(0, 10).split('-').reverse().join('/') : '—';
const formatAmount = value =>
  value === null || value === undefined
    ? '—'
    : Number(value).toLocaleString('es-AR', {
        style: 'currency',
        currency: 'ARS',
      });

const hasSubTypes = computed(() => subTypeOptions.value.length > 0);

const selectedSubTypeObj = computed(() => {
  const r = selectedReasonObj.value;
  return (r?.sub_types || []).find(st => st.value === selectedSubType.value);
});

// Nivel 3: ¿muestra formulario de acuerdo?
const requiresPaymentForm = computed(() =>
  selectedSubTypeObj.value?.requires_form === 'payment_agreement'
);
const agreementType = computed(() => selectedSubTypeObj.value?.agreement_type || '');

const isPlanPagos  = computed(() => agreementType.value === 'plan_pagos');
const isDescuento  = computed(() => agreementType.value === 'descuento');
const isPagoSimple = computed(() => ['pago_total', 'pago_parcial'].includes(agreementType.value));

const REASONS_WITHOUT_MANAGED_AMOUNT = new Set([
  'fallecido', 'desconoce_deuda', 'se_niega_a_abonar',
  'problemas_economicos', 'dato_de_contacto_erroneo',
  'denuncia_defensa_consumidor',
]);

// SICARG — "Registro completo obligatorio" (closure_settings.reasons[].requires_full_record):
// todos los campos que el formulario muestra para la categoría pasan a ser obligatorios.
// Apagado por defecto. Validación solo frontend (el bot de n8n cierra por la misma API).
const fullRecordRequired = computed(
  () => selectedReasonObj.value?.requires_full_record === true
);

const amountVisibleByDefault = computed(
  () =>
    settings.value.show_managed_amount &&
    !REASONS_WITHOUT_MANAGED_AMOUNT.has(selectedReason.value)
);

// SICARG — obligatoriedad del monto configurable por categoría desde
// Configuración → Clasificación de cierres (closure_settings.reasons[].requires_managed_amount).
const managedAmountRequired = computed(
  () =>
    selectedReasonObj.value?.requires_managed_amount === true ||
    (fullRecordRequired.value && amountVisibleByDefault.value)
);

const showManagedAmount = computed(() =>
  // Si la categoría exige monto, el campo se muestra sí o sí (aunque esté en el
  // Set de categorías sin monto o el admin haya ocultado el campo globalmente).
  managedAmountRequired.value || amountVisibleByDefault.value
);

// Etiqueta dinámica: "Pago ya realizado" usa "Pago efectuado" en lugar de "Compromiso a Pagar".
const managedAmountLabel = computed(() =>
  selectedReason.value === 'cliente_indica_pago_ya_realizado'
    ? t('CONVERSATION_CLOSURE.MODAL.FIELDS.PAGO_EFECTUADO')
    : t('CONVERSATION_CLOSURE.MODAL.FIELDS.MANAGED_AMOUNT')
);

// ─── Cálculo automático de cuotas (plan de pagos) ────────────────────
const calcularCuotas = () => {
  const total   = parseAmount(agreementForm.monto_total) || 0;
  const cuotas  = parseInt(agreementForm.cantidad_cuotas) || 0;
  const primera = agreementForm.fecha;

  if (!total || !cuotas || cuotas < 1) {
    installments.value = [];
    agreementForm.valor_cuota_sugerido = '';
    return;
  }

  const valorBase = parseFloat((total / cuotas).toFixed(2));
  agreementForm.valor_cuota_sugerido = valorBase;

  const lista = [];
  for (let i = 0; i < cuotas; i++) {
    const fechaVenc = primera ? addMonthsBA(primera, i) : '';
    lista.push({
      numero_cuota:     i + 1,
      fecha_vencimiento: fechaVenc,
      monto_cuota:      valorBase,
    });
  }
  installments.value = lista;
};

// Suma de cuotas (para el indicador visual)
const sumaInstallments = computed(() =>
  installments.value.reduce((acc, c) => acc + (parseAmount(c.monto_cuota) || 0), 0)
);
const diferenciaCuotas = computed(() =>
  Math.abs((parseAmount(agreementForm.monto_total) || 0) - sumaInstallments.value)
);
const cuotasSuman = computed(() =>
  diferenciaCuotas.value < 0.01 ? 'ok' : 'diff'
);

watch([() => agreementForm.monto_total, () => agreementForm.cantidad_cuotas, () => agreementForm.fecha],
  calcularCuotas,
  { immediate: false }
);

// Auto-calcula "Compromiso a Pagar" en descuento cuando cambia Deuda Total o %.
// El usuario puede editarlo manualmente después del cálculo.
watch([() => agreementForm.monto_total, () => agreementForm.valor_descuento], () => {
  if (!isDescuento.value) return;
  const total = parseAmount(agreementForm.monto_total) || 0;
  const pct   = parseFloat(agreementForm.valor_descuento) || 0;
  if (total > 0 && pct > 0) {
    agreementForm.monto_acordado_final = (total * (1 - pct / 100)).toFixed(2);
  }
});

// ─── Reset y cascada ─────────────────────────────────────────────────
watch(selectedReason, () => {
  if (isPresettingClassification.value) return;
  selectedSubType.value = '';
  resetAgreementForm();
});
watch(selectedReason, () => {
  duplicateAction.value = '';
});
watch(selectedSubType, () => {
  resetAgreementForm();
  // Precargar "hoy en Buenos Aires" como fecha por defecto
  if (requiresPaymentForm.value) {
    agreementForm.fecha = todayBuenosAires();
  }
});

const resetAgreementForm = () => {
  Object.assign(agreementForm, {
    tipo_acuerdo: '',
    monto_total: '',
    cantidad_cuotas: '',
    valor_cuota_sugerido: '',
    fecha: '',
    tipo_descuento: '',
    valor_descuento: '',
    monto_acordado_final: '',
  });
  installments.value = [];
};

const resetForm = () => {
  selectedReason.value = '';
  selectedSubType.value = '';
  managedAmount.value = '';
  notes.value = '';
  duplicateAction.value = '';
  resetAgreementForm();
};

// ─── Validación ──────────────────────────────────────────────────────
const isRequired = computed(() => settings.value.require_on_resolve);

const isManagedAmountMissing = computed(
  () => managedAmountRequired.value && !(parseAmount(managedAmount.value) > 0)
);

// SICARG — texto de monto con formato no interpretable (ej. letras) en cualquier campo de monto.
const hasInvalidAmount = computed(
  () =>
    isAmountInvalid(managedAmount.value) ||
    isAmountInvalid(agreementForm.monto_total) ||
    isAmountInvalid(agreementForm.monto_acordado_final) ||
    installments.value.some(c => isAmountInvalid(c.monto_cuota))
);

// SICARG — registro completo: subtipo y observaciones (el monto va por managedAmountRequired).
const isFullRecordIncomplete = computed(
  () =>
    fullRecordRequired.value &&
    ((hasSubTypes.value && !selectedSubType.value) ||
      (settings.value.show_notes && !notes.value.trim()))
);

const isDuplicateUnresolved = computed(
  () => duplicateRecords.value.length > 0 && !duplicateAction.value
);

const validationMessage = computed(() => {
  if (isDuplicateUnresolved.value)
    return t('CONVERSATION_CLOSURE.MODAL.DUPLICATE.CHOOSE');
  if (hasInvalidAmount.value) return t('CONVERSATION_CLOSURE.MODAL.VALIDATION_AMOUNT_INVALID');
  if (isManagedAmountMissing.value) return t('CONVERSATION_CLOSURE.MODAL.VALIDATION_MANAGED_AMOUNT_REQUIRED');
  if (isFullRecordIncomplete.value) return t('CONVERSATION_CLOSURE.MODAL.VALIDATION_FULL_RECORD_REQUIRED');
  return t('CONVERSATION_CLOSURE.MODAL.VALIDATION_REQUIRED');
});

// Vista previa del valor interpretado bajo cada campo de monto (o aviso si no se entiende).
const amountHint = raw => (String(raw ?? '').trim() === '' ? '' : formatAmountPreview(raw));

const isFormValid = computed(() => {
  if (isRequired.value && !selectedReason.value) return false;
  if (hasInvalidAmount.value) return false;
  if (isManagedAmountMissing.value) return false;
  if (isFullRecordIncomplete.value) return false;
  if (isDuplicateUnresolved.value) return false;
  if (requiresPaymentForm.value) {
    if (isPlanPagos.value) {
      return !!(parseAmount(agreementForm.monto_total) && agreementForm.cantidad_cuotas && agreementForm.fecha && installments.value.length > 0);
    }
    if (isDescuento.value) {
      return !!(parseAmount(agreementForm.monto_total) && agreementForm.valor_descuento && parseAmount(agreementForm.monto_acordado_final) && agreementForm.fecha);
    }
    if (isPagoSimple.value) {
      return !!(parseAmount(agreementForm.monto_total) && agreementForm.fecha);
    }
  }
  return true;
});

// ─── Construcción del payload ─────────────────────────────────────────
const buildPayload = () => {
  const closure = {
    closure_reason:     selectedReason.value || null,
    closure_sub_reason: selectedSubType.value || null,
    notes:              notes.value || null,
  };
  if (showManagedAmount.value && parseAmount(managedAmount.value) !== null) {
    closure.managed_amount = parseAmount(managedAmount.value);
  }

  let payment_agreement = null;
  if (requiresPaymentForm.value && agreementType.value) {
    payment_agreement = { tipo_acuerdo: agreementType.value, frecuencia: 'mensual' };

    if (isPlanPagos.value) {
      payment_agreement.monto_total_comprometido = parseAmount(agreementForm.monto_total);
      payment_agreement.cantidad_cuotas          = parseInt(agreementForm.cantidad_cuotas);
      payment_agreement.valor_cuota              = parseFloat(agreementForm.valor_cuota_sugerido);
      payment_agreement.fecha_relevante          = agreementForm.fecha;
      payment_agreement.installments             = installments.value.map(c => ({
        numero_cuota:     c.numero_cuota,
        fecha_vencimiento: c.fecha_vencimiento,
        monto_cuota:       parseAmount(c.monto_cuota),
      }));
    } else if (isDescuento.value) {
      payment_agreement.tipo_descuento           = 'porcentaje';
      payment_agreement.valor_descuento          = parseFloat(agreementForm.valor_descuento);
      payment_agreement.monto_acordado_final     = parseAmount(agreementForm.monto_acordado_final);
      payment_agreement.monto_total_comprometido = parseAmount(agreementForm.monto_total);
      payment_agreement.fecha_relevante          = agreementForm.fecha;
    } else if (isPagoSimple.value) {
      payment_agreement.monto_total_comprometido = parseAmount(agreementForm.monto_total);
      payment_agreement.fecha_relevante          = agreementForm.fecha;
    }
  }

  return { closure, payment_agreement };
};

// ─── API ─────────────────────────────────────────────────────────────
const handleConfirm = async () => {
  if (!isFormValid.value) {
    useAlert(validationMessage.value);
    return;
  }
  isSaving.value = true;
  try {
    const { closure, payment_agreement } = buildPayload();
    await ClosureAPI.createConversationClosure(conversationId.value, closure, payment_agreement);
    // "Sobrescribir": el registro nuevo ya quedó guardado, recién ahí se elimina el anterior.
    if (duplicateAction.value === 'overwrite') {
      await Promise.all(
        duplicateRecords.value.map(r => ClosureAPI.deletePaymentClosure(r.id, conversationId.value))
      );
    }
    emit('saved');
    dialogRef.value?.close();
  } catch {
    useAlert(t('CONVERSATION_CLOSURE.MODAL.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const open = async (closureSettings, convId, convContactId) => {
  if (closureSettings) settings.value = { ...settings.value, ...closureSettings };
  conversationId.value = convId;
  contactId.value = convContactId;
  monthPayments.value = [];
  currentAgreement.value = null;
  resetForm();
  dialogRef.value?.open();
  if (contactId.value) {
    ClosureAPI.getContactMonthPayments(contactId.value)
      .then(({ data }) => {
        monthPayments.value = data;
      })
      .catch(() => {});
  }
  // Carga en background: si la conv. ya tiene cierre is_latest con acuerdo, muestra aviso.
  try {
    const { data } = await ClosureAPI.getConversationClosure(convId);
    if (data?.payment_agreement) currentAgreement.value = data.payment_agreement;
    // SICARG — precargar la última clasificación cargada para este caso (por
    // cualquier agente), para que un caso que se reabre y vuelve a cerrarse
    // en poco tiempo no obligue a tipificar de nuevo desde cero.
    if (data?.closure_reason) {
      isPresettingClassification.value = true;
      selectedReason.value = data.closure_reason;
      selectedSubType.value = data.closure_sub_reason || '';
      await nextTick();
      isPresettingClassification.value = false;
    }
  } catch { /* sin cierre previo — ignorar */ }
};

defineExpose({ open, close: () => dialogRef.value?.close() });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="3xl"
    :title="t('CONVERSATION_CLOSURE.MODAL.TITLE')"
    :description="t('CONVERSATION_CLOSURE.MODAL.DESCRIPTION')"
    :confirm-button-label="t('CONVERSATION_CLOSURE.MODAL.ACTIONS.CONFIRM')"
    :cancel-button-label="t('CONVERSATION_CLOSURE.MODAL.ACTIONS.CANCEL')"
    :is-loading="isSaving"
    :disable-confirm-button="!isFormValid || isSaving"
    @confirm="handleConfirm"
  >
    <div class="flex flex-col gap-4 max-h-[60vh] overflow-y-auto pr-1">

      <!-- Aviso: ya existe un acuerdo activo que será reemplazado -->
      <div
        v-if="currentAgreement"
        class="flex gap-2 p-3 rounded-lg bg-n-amber-3 border border-n-amber-6"
        role="alert"
      >
        <span class="i-lucide-alert-triangle text-base text-n-amber-9 shrink-0 mt-0.5" />
        <p class="text-sm text-n-amber-11">
          {{ t('CONVERSATION_CLOSURE.MODAL.REPLACE_AGREEMENT_WARNING', {
            tipo: TIPO_LABELS[currentAgreement.tipo_acuerdo] || currentAgreement.tipo_acuerdo,
            monto: Number(currentAgreement.monto_acordado_final ?? currentAgreement.monto_total_comprometido ?? 0).toFixed(2)
          }) }}
        </p>
      </div>

      <!-- Nivel 1: Categoría de tipificación -->
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('CONVERSATION_CLOSURE.MODAL.FIELDS.REASON') }}
          <span v-if="isRequired" class="text-n-ruby-9">*</span>
        </label>
        <ComboBox
          v-model="selectedReason"
          :options="reasonOptions"
          :placeholder="t('CONVERSATION_CLOSURE.MODAL.PLACEHOLDERS.REASON')"
          class="w-full"
        />
      </div>

      <!-- Aviso de doble carga: el cliente ya tiene un registro del mismo tipo en el mes -->
      <div
        v-if="duplicateRecords.length"
        class="flex flex-col gap-3 p-3 rounded-lg bg-n-amber-3 border border-n-amber-6"
        role="alert"
      >
        <p class="mb-0 text-sm font-medium text-n-amber-11">
          {{ t('CONVERSATION_CLOSURE.MODAL.DUPLICATE.TITLE') }}
        </p>
        <ul class="flex flex-col gap-2 p-0 m-0 list-none">
          <li
            v-for="record in duplicateRecords"
            :key="record.id"
            class="grid grid-cols-2 gap-x-4 gap-y-1 p-2 text-xs rounded-md bg-n-alpha-black2 text-n-slate-12 sm:grid-cols-3"
          >
            <span>
              <b>{{ t('CONVERSATION_CLOSURE.MODAL.DUPLICATE.TYPE') }}:</b>
              {{ record.closure_reason_label }}
            </span>
            <span>
              <b>{{ t('CONVERSATION_CLOSURE.MODAL.DUPLICATE.AMOUNT') }}:</b>
              {{ formatAmount(record.managed_amount ?? record.agreement_amount) }}
            </span>
            <span>
              <b>{{ t('CONVERSATION_CLOSURE.MODAL.DUPLICATE.DATE') }}:</b>
              {{ formatDate(record.date) }}
            </span>
            <span>
              <b>{{ t('CONVERSATION_CLOSURE.MODAL.DUPLICATE.LOADED_BY') }}:</b>
              {{ record.created_by || '—' }}
            </span>
            <span class="sm:col-span-2">
              <b>{{ t('CONVERSATION_CLOSURE.MODAL.DUPLICATE.LOADED_AT') }}:</b>
              {{ formatDate(record.closed_at) }}
            </span>
          </li>
        </ul>
        <label class="flex gap-2 items-center text-sm text-n-slate-12">
          <input v-model="duplicateAction" type="radio" value="keep" />
          {{ t('CONVERSATION_CLOSURE.MODAL.DUPLICATE.KEEP') }}
        </label>
        <label class="flex gap-2 items-center text-sm text-n-slate-12">
          <input v-model="duplicateAction" type="radio" value="overwrite" />
          {{ t('CONVERSATION_CLOSURE.MODAL.DUPLICATE.OVERWRITE') }}
        </label>
      </div>

      <!-- Nivel 2: Subtipo (cascada) -->
      <div v-if="hasSubTypes" class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('CONVERSATION_CLOSURE.MODAL.FIELDS.SUB_TYPE') }}
          <span v-if="fullRecordRequired" class="text-n-ruby-9">*</span>
        </label>
        <ComboBox
          v-model="selectedSubType"
          :options="subTypeOptions"
          :placeholder="t('CONVERSATION_CLOSURE.MODAL.PLACEHOLDERS.SUB_TYPE')"
          class="w-full"
        />
      </div>

      <!-- Nivel 3: Formulario dinámico de acuerdo de pago -->
      <template v-if="requiresPaymentForm && selectedSubType">

        <!-- Separador visual -->
        <div class="border-t border-n-weak pt-2">
          <p class="text-xs font-semibold text-n-slate-10 uppercase tracking-wide">
            {{ t('CONVERSATION_CLOSURE.MODAL.PAYMENT_AGREEMENT.TITLE') }}
          </p>
        </div>

        <!-- PAGO TOTAL / PAGO PARCIAL -->
        <template v-if="isPagoSimple">
          <div class="flex flex-col gap-2">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('CONVERSATION_CLOSURE.MODAL.FIELDS.MONTO') }} <span class="text-n-ruby-9">*</span>
            </label>
            <div class="flex gap-2 items-stretch">
              <span class="flex items-center px-3 text-sm font-medium rounded-lg bg-n-alpha-black2 text-n-slate-11">$</span>
              <Input v-model="agreementForm.monto_total" type="text" inputmode="decimal" size="md" class="flex-1" placeholder="0.00" />
            </div>
          </div>
          <div class="flex flex-col gap-2">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('CONVERSATION_CLOSURE.MODAL.FIELDS.FECHA_PAGO') }} <span class="text-n-ruby-9">*</span>
            </label>
            <Input v-model="agreementForm.fecha" type="date" size="md" />
          </div>
        </template>

        <!-- DESCUENTO: Deuda Total → Descuento (%) → Compromiso a Pagar (autocalc) → Fecha -->
        <template v-if="isDescuento">
          <div class="flex flex-col gap-2">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('CONVERSATION_CLOSURE.MODAL.FIELDS.MONTO_TOTAL') }} <span class="text-n-ruby-9">*</span>
            </label>
            <div class="flex gap-2 items-stretch">
              <span class="flex items-center px-3 text-sm font-medium rounded-lg bg-n-alpha-black2 text-n-slate-11">$</span>
              <Input v-model="agreementForm.monto_total" type="text" inputmode="decimal" size="md" class="flex-1" placeholder="0.00" />
            </div>
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div class="flex flex-col gap-2">
              <label class="text-sm font-medium text-n-slate-12">
                {{ t('CONVERSATION_CLOSURE.MODAL.FIELDS.VALOR_DESCUENTO') }} <span class="text-n-ruby-9">*</span>
              </label>
              <Input v-model="agreementForm.valor_descuento" type="number" size="md" placeholder="0" />
            </div>
            <div class="flex flex-col gap-2">
              <label class="text-sm font-medium text-n-slate-12">
                {{ t('CONVERSATION_CLOSURE.MODAL.FIELDS.MONTO_ACORDADO_FINAL') }} <span class="text-n-ruby-9">*</span>
              </label>
              <div class="flex gap-2 items-stretch">
                <span class="flex items-center px-3 text-sm font-medium rounded-lg bg-n-alpha-black2 text-n-slate-11">$</span>
                <Input v-model="agreementForm.monto_acordado_final" type="text" inputmode="decimal" size="md" class="flex-1" placeholder="0.00" />
              </div>
            </div>
          </div>
          <div class="flex flex-col gap-2">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('CONVERSATION_CLOSURE.MODAL.FIELDS.FECHA_PAGO') }} <span class="text-n-ruby-9">*</span>
            </label>
            <Input v-model="agreementForm.fecha" type="date" size="md" />
          </div>
        </template>

        <!-- PLAN DE PAGOS -->
        <template v-if="isPlanPagos">
          <div class="grid grid-cols-2 gap-3">
            <div class="flex flex-col gap-2">
              <label class="text-sm font-medium text-n-slate-12">
                {{ t('CONVERSATION_CLOSURE.MODAL.FIELDS.MONTO_TOTAL') }} <span class="text-n-ruby-9">*</span>
              </label>
              <div class="flex gap-2 items-stretch">
                <span class="flex items-center px-3 text-sm font-medium rounded-lg bg-n-alpha-black2 text-n-slate-11">$</span>
                <Input v-model="agreementForm.monto_total" type="text" inputmode="decimal" size="md" class="flex-1" placeholder="0.00" />
              </div>
            </div>
            <div class="flex flex-col gap-2">
              <label class="text-sm font-medium text-n-slate-12">
                {{ t('CONVERSATION_CLOSURE.MODAL.FIELDS.CANTIDAD_CUOTAS') }} <span class="text-n-ruby-9">*</span>
              </label>
              <Input v-model="agreementForm.cantidad_cuotas" type="number" size="md" min="1" placeholder="Ej: 3" />
            </div>
          </div>
          <div class="flex flex-col gap-2">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('CONVERSATION_CLOSURE.MODAL.FIELDS.FECHA_PRIMER_VENCIMIENTO') }} <span class="text-n-ruby-9">*</span>
            </label>
            <Input v-model="agreementForm.fecha" type="date" size="md" />
          </div>

          <!-- Preview de cuotas con edición individual -->
          <div v-if="installments.length > 0" class="flex flex-col gap-2">
            <div class="flex items-center justify-between">
              <p class="text-sm font-medium text-n-slate-12">
                {{ t('CONVERSATION_CLOSURE.MODAL.PLAN.PREVIEW_TITLE') }}
              </p>
              <!-- Indicador visual: suma vs total (aviso, no bloqueo) -->
              <span
                class="text-xs px-2 py-0.5 rounded-full"
                :class="cuotasSuman === 'ok'
                  ? 'bg-n-teal-3 text-n-teal-11'
                  : 'bg-n-amber-3 text-n-amber-11'"
              >
                {{ cuotasSuman === 'ok'
                  ? t('CONVERSATION_CLOSURE.MODAL.PLAN.CUOTAS_OK')
                  : t('CONVERSATION_CLOSURE.MODAL.PLAN.CUOTAS_DIFF', { diff: diferenciaCuotas.toFixed(2) }) }}
              </span>
            </div>

            <div class="flex flex-col gap-1 max-h-48 overflow-y-auto pr-1">
              <div
                v-for="cuota in installments"
                :key="cuota.numero_cuota"
                class="flex items-center gap-2 py-1 text-sm"
              >
                <span class="w-16 text-n-slate-10 text-xs shrink-0">
                  {{ t('CONVERSATION_CLOSURE.MODAL.PLAN.CUOTA_N', { n: cuota.numero_cuota }) }}
                </span>
                <span class="w-28 text-n-slate-11 text-xs shrink-0">{{ cuota.fecha_vencimiento || '—' }}</span>
                <div class="flex gap-1 items-stretch flex-1">
                  <span class="flex items-center px-2 text-xs rounded bg-n-alpha-black2 text-n-slate-11">$</span>
                  <Input
                    v-model="cuota.monto_cuota"
                    type="text"
                    inputmode="decimal"
                    size="sm"
                    class="flex-1"
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>
          </div>
        </template>

      </template>

      <!-- Compromiso a Pagar (deuda total de la cuenta, independiente del acuerdo) -->
      <div v-if="showManagedAmount" class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ managedAmountLabel }}
          <span v-if="managedAmountRequired" class="text-n-ruby-9">*</span>
        </label>
        <div class="flex gap-2 items-stretch">
          <span class="flex items-center px-3 text-sm font-medium rounded-lg bg-n-alpha-black2 text-n-slate-11">$</span>
          <Input v-model="managedAmount" type="text" inputmode="decimal" size="md" class="flex-1" :placeholder="t('CONVERSATION_CLOSURE.MODAL.PLACEHOLDERS.MANAGED_AMOUNT')" />
        </div>
        <p v-if="amountHint(managedAmount)" class="mb-0 text-xs text-n-slate-10">
          {{ amountHint(managedAmount) }}
        </p>
      </div>

      <!-- Observaciones -->
      <div v-if="settings.show_notes" class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('CONVERSATION_CLOSURE.MODAL.FIELDS.NOTES') }}
          <span v-if="fullRecordRequired" class="text-n-ruby-9">*</span>
        </label>
        <TextArea v-model="notes" class="w-full" :placeholder="t('CONVERSATION_CLOSURE.MODAL.PLACEHOLDERS.NOTES')" />
      </div>

    </div>
  </Dialog>
</template>
